import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { compose } from 'ramda';
import styled from 'styled-components/macro';
import Grid from '@material-ui/core/Grid';
import {
  showToast,
  BackTo,
  Typography,
  Switch,
  Button,
  Tooltip,
} from '@deeptrue-frontend/ui';

import { Dashboard } from 'components/Layout';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { ProjectType } from 'store/modules/projects/projects.types';
import { OrganizationType } from 'store/modules/auth/auth';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import api from 'api/api';
import { useFormatMessage } from 'components/IntlProvider';
import { withProject } from '../../withProject';
import ProjectInviteCSVRespondents from './ProjectInviteCSVRespondents';
import { ProjectStatus } from 'consts/statuses';

const { connect, selectors } = StoreProvider;

const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 2rem;
`;

const Footer = styled.div`
  border-top: 1px solid #e3e5e8;
  padding-top: 1.5rem;
  margin-top: 3rem;
  display: flex;
  justify-content: flex-end;
`;

const Subtitle = styled(Typography)`
  font-size: 0.875rem;
  font-weight: 400;
  color: #5c6370;
`;

const SwitchSection = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 1rem;
  margin-bottom: 1.375rem;
`;

interface ProjectSurveyProps
  extends RouteComponentProps<{ id: string; codename: string }> {
  project: ProjectType;
  organization: OrganizationType;
}

const CSVDataCollection = ({
  project,
  organization,
  location,
  match: { params },
  history,
}: ProjectSurveyProps) => {
  const [surveyUiid, setSurveyUiid] = useState<string | null>(null);
  const [areAvailableSurveys, setAvailableSurveys] = useState(false);
  const t = useFormatMessage();
  const [isReminderChecked, setIsReminderChecked] = useState<
    string | undefined
  >(project.send_reminders);

  const [respondentsList, setRespondentsList] = useState({
    count: 0,
    results: [],
    next: null,
  });
  const [areRespondentsAdded, setRespondentsAdded] = useState(false);
  const isReminderAvailable =
    project.status.value === ProjectStatus.OFFER_ACCEPTED;

  useEffect(() => {
    setIsReminderChecked(project.send_reminders ? 'trye' : undefined);
  }, [project.send_reminders]);

  useEffect(() => {
    if (project.id !== -1) {
      api.resources
        .getProjectSurveys({
          orgName: params.codename || organization.codename,
          uuid: project.uuid,
        })
        .then(({ data }) => {
          if (data.results.length) {
            const { uuid } = data.results[0];

            setSurveyUiid(uuid);
            setAvailableSurveys(true);
          }
        })
        .catch(() => {
          showToast({
            type: 'error',
            title: t(
              'errors.toast.general',
              'Something went wrong, please contact administrator'
            ),
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  useEffect(() => {
    if (surveyUiid) {
      api.resources
        .getRespondentsList({
          uuid: surveyUiid,
          parsedSearch: { ordering: '-Created at', limit: 1 },
        })
        .then(({ data }: any) => {
          if (data) {
            setRespondentsAdded(data.count > 0);
            setRespondentsList({
              ...data,
              results: [...respondentsList.results, ...data.results],
            });
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyUiid]);

  const toggleReminder = () => {
    if (isReminderChecked) {
      setIsReminderChecked(undefined);
      return api.resources
        .updateProjectReminder({
          organizationCodename: params.codename || organization.codename,
          project: {
            ...project,
            send_reminders: false,
          },
        })
        .catch(() => {
          setIsReminderChecked('true');
        });
    }
    setIsReminderChecked('true');
    api.resources
      .updateProjectReminder({
        organizationCodename: params.codename || organization.codename,
        project: {
          ...project,
          send_reminders: true,
        },
      })
      .catch(() => {
        setIsReminderChecked(undefined);
      });
  };

  const templateUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/collect-data/template`
    : `/project/${project.id}/collect-data/template`;
  const collectDataUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/collect-data`
    : `/project/${project.id}/collect-data`;

  return (
    <Dashboard
      title={project.name || t('page.project', 'Project details')}
      location={location}
      isFullWidth
    >
      <BackTo onClick={() => history.push(collectDataUrl)}>
        {t('project.collectData.backTo', 'Back to collect data')}
      </BackTo>

      {areAvailableSurveys ? (
        <Grid container spacing={3} style={{ maxWidth: '39rem' }}>
          <Grid item xs={12}>
            <ProjectInviteCSVRespondents
              project={project}
              codename={params.codename}
              uuid={surveyUiid}
              setRespondentsAdded={setRespondentsAdded}
            />
            {isReminderAvailable && (
              <>
                <TooltipWrapper>
                  <Typography
                    variant="copy1"
                    weight="medium"
                    color="253858"
                    mr="0.5rem"
                  >
                    {t('templates.base.reminder.title', 'Reminder Email')}
                  </Typography>
                  <Tooltip text={t('templates.reminder.tooltip', '')} />
                </TooltipWrapper>

                <Subtitle mb="1rem">
                  {t(
                    'templates.base.reminder.desc',
                    'Do you want to sent reminder email after 7 days?'
                  )}
                </Subtitle>
                <SwitchSection>
                  <Switch onChange={toggleReminder} value={isReminderChecked} />
                </SwitchSection>
              </>
            )}
            <Footer>
              <Button
                disabled={!areRespondentsAdded}
                onClick={() => history.push(templateUrl)}
              >
                {t('templates.base.continue', 'Continue')}
              </Button>
            </Footer>
          </Grid>
        </Grid>
      ) : (
        <Typography variant="copy1">
          {t('pages.project.nosurveys', 'No surveys available')}
        </Typography>
      )}
    </Dashboard>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
  })),
  withMetaDescription({ title: 'Dashboard projects' }),
  withProject(({ match, organization }: ProjectSurveyProps) => ({
    projectId: match.params.id,
    organizationCodename: match.params.codename || organization.codename,
  }))
)(CSVDataCollection);
